<template>
  <div class="field-list">
    <div v-if="!fields.length" class="field-list__empty">
      Aún no hay campos. Selecciona un tipo y haz click sobre el PDF.
    </div>

    <ul v-else class="field-list__items">
      <li
        v-for="field in fields"
        :key="field.id"
        class="field-list__item"
        :class="{ 'is-selected': field.id === selectedId }"
        @click="$emit('select', field.id)"
      >
        <div class="field-list__row">
          <span class="field-list__badge" :class="`badge--${field.type.toLowerCase()}`">
            {{ typeLabel(field.type) }}
          </span>
          <span class="field-list__name" :title="field.name">{{ field.label }}</span>
          <span class="field-list__page">pág. {{ field.page }}</span>
          <button
            type="button"
            class="field-list__del"
            title="Eliminar campo"
            @click.stop="$emit('delete', field.id)"
          >
            ×
          </button>
        </div>

        <div
          v-if="field.id === selectedId"
          class="field-list__config"
          @click.stop
        >
          <label class="field-list__cfg-label">Etiqueta</label>
          <input
            class="field-list__cfg-input"
            type="text"
            :value="field.label"
            @input="updateField(field, 'label', $event.target.value)"
          />

          <label class="field-list__cfg-label field-list__cfg-label--help">
            Clave de dato (opcional)
            <feather-icon
              icon="HelpCircleIcon"
              size="13"
              class="field-list__cfg-help-icon"
              v-b-tooltip.hover.right="linkKeyTooltip"
            />
          </label>
          <input
            class="field-list__cfg-input"
            type="text"
            placeholder="ej: rut"
            :value="field.linkKey"
            @input="updateField(field, 'linkKey', $event.target.value)"
          />

          <template v-if="isDate(field)">
            <label class="field-list__cfg-label">Formato de fecha</label>
            <select
              class="field-list__cfg-input"
              :value="field.dateFormat || defaultDateFormat"
              @change="updateField(field, 'dateFormat', $event.target.value)"
            >
              <option
                v-for="opt in dateFormats"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </template>

          <template v-if="isSelect(field)">
            <label class="field-list__cfg-label">Opciones de la lista</label>
            <div class="field-list__opt-head">
              <span>Etiqueta (se muestra)</span>
              <span>Valor (se estampa)</span>
            </div>
            <div
              v-for="(opt, i) in optionsOf(field)"
              :key="i"
              class="field-list__opt-row"
            >
              <input
                class="field-list__cfg-input field-list__opt-input"
                type="text"
                :value="opt.label"
                :placeholder="`Opción ${i + 1}`"
                @input="updateOption(field, i, 'label', $event.target.value)"
              />
              <input
                class="field-list__cfg-input field-list__opt-input"
                type="text"
                :value="opt.value"
                :placeholder="`Valor ${i + 1}`"
                @input="updateOption(field, i, 'value', $event.target.value)"
              />
              <button
                type="button"
                class="field-list__opt-del"
                title="Quitar opción"
                @click="removeOption(field, i)"
              >
                ×
              </button>
            </div>
            <button
              type="button"
              class="field-list__opt-add"
              @click="addOption(field)"
            >
              + Agregar opción
            </button>
          </template>

          <template v-if="!isCheckbox(field)">
            <label class="field-list__cfg-label">Texto de ayuda (placeholder)</label>
            <input
              class="field-list__cfg-input"
              type="text"
              :value="field.placeholder"
              :placeholder="field.label"
              @input="updateField(field, 'placeholder', $event.target.value)"
            />

            <label class="field-list__cfg-label">Valor por defecto</label>
            <input
              class="field-list__cfg-input"
              type="text"
              :value="field.defaultValue == null ? '' : field.defaultValue"
              @input="updateField(field, 'defaultValue', $event.target.value || null)"
            />

            <div class="field-list__cfg-grid">
              <div>
                <label class="field-list__cfg-label">Tamaño fuente</label>
                <input
                  class="field-list__cfg-input"
                  type="number"
                  min="6"
                  max="72"
                  :value="styleOf(field).fontSize"
                  @input="updateStyle(field, 'fontSize', toSize($event.target.value))"
                />
              </div>
              <div>
                <label class="field-list__cfg-label">Alineación</label>
                <select
                  class="field-list__cfg-input"
                  :value="styleOf(field).alignment"
                  @change="updateStyle(field, 'alignment', $event.target.value)"
                >
                  <option value="left">Izquierda</option>
                  <option value="center">Centro</option>
                  <option value="right">Derecha</option>
                </select>
              </div>
            </div>
          </template>

          <label class="field-list__cfg-check">
            <input
              type="checkbox"
              :checked="field.required"
              @change="updateField(field, 'required', $event.target.checked)"
            />
            Requerido
          </label>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>

import {
  defaultLabelForType,
  FIELD_TYPES,
  DATE_FORMATS,
  DEFAULT_DATE_FORMAT,
} from '@/febos/global/pdf/formBuilder/model/fieldModel';

const DEFAULT_FONT_SIZE = 10;
const DEFAULT_ALIGNMENT = 'left';

/**
 * Listado de campos agregados con edición de `label` y `required`.
 * No muta el array directamente: emite `update` con el campo modificado para
 * que el contenedor (dueño del v-model) lo reemplace.
 *
 * Props:
 *  - fields: PdfField[]
 *  - selectedId: id del campo seleccionado
 * Emite:
 *  - select(id)
 *  - delete(id)
 *  - update(updatedField)
 */
export default {
  name: 'FieldListPanel',
  props: {
    fields: { type: Array, default: () => [] },
    selectedId: { type: String, default: null },
  },
  data() {
    return {
      linkKeyTooltip: 'Vincula campos que comparten el mismo dato. '
        + 'Si pones la misma clave (ej: "rut") en varios campos —aunque estén en '
        + 'páginas distintas—, al escribir el valor en uno se completa en todos '
        + 'automáticamente. Si la dejas vacía, el campo es independiente.',
      dateFormats: DATE_FORMATS,
      defaultDateFormat: DEFAULT_DATE_FORMAT,
    };
  },
  methods: {
    typeLabel(type) {
      return defaultLabelForType(type);
    },
    isCheckbox(field) {
      return field.type === FIELD_TYPES.CHECKBOX;
    },
    isSelect(field) {
      return field.type === FIELD_TYPES.SELECT;
    },
    isDate(field) {
      return field.type === FIELD_TYPES.DATE;
    },
    optionsOf(field) {
      return Array.isArray(field.options) ? field.options : [];
    },
    addOption(field) {
      const options = [...this.optionsOf(field), { label: '', value: '' }];
      this.$emit('update', { ...field, options });
    },
    updateOption(field, index, key, value) {
      const options = this.optionsOf(field).map((opt, i) => (
        i === index ? { ...opt, [key]: value } : opt
      ));
      this.$emit('update', { ...field, options });
    },
    removeOption(field, index) {
      const options = this.optionsOf(field).filter((_, i) => i !== index);
      this.$emit('update', { ...field, options });
    },
    // Estilo del campo con defaults, para que los controles siempre tengan valor.
    styleOf(field) {
      return {
        fontSize: DEFAULT_FONT_SIZE,
        alignment: DEFAULT_ALIGNMENT,
        ...(field.style || {}),
      };
    },
    toSize(raw) {
      const n = parseInt(raw, 10);
      if (Number.isNaN(n)) {
        return DEFAULT_FONT_SIZE;
      }
      return Math.min(72, Math.max(6, n));
    },
    updateField(field, key, value) {
      this.$emit('update', { ...field, [key]: value });
    },
    // Actualiza una propiedad anidada de `style` sin perder el resto.
    updateStyle(field, key, value) {
      this.$emit('update', {
        ...field,
        style: { ...this.styleOf(field), [key]: value },
      });
    },
  },
};

</script>

<style lang="scss" scoped>

.field-list {
  display: flex;
  flex-direction: column;
}

.field-list__empty {
  font-size: 12px;
  color: #8a8aa0;
  padding: 8px 0;
}

.field-list__items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field-list__item {
  border: 1px solid #e3e3ef;
  border-radius: 6px;
  background: #fff;
  padding: 6px 8px;
  cursor: pointer;
}

.field-list__item.is-selected {
  border-color: #7367f0;
  box-shadow: 0 0 0 1px #7367f0;
}

.field-list__row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.field-list__badge {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 3px;
  background: #ede9fe;
  color: #5a4fd1;
  white-space: nowrap;
}

.field-list__badge.badge--signature,
.field-list__badge.badge--initials {
  background: #d9f7e8;
  color: #1a9b5f;
}

.field-list__name {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #30304d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-list__page {
  font-size: 10px;
  color: #9a9ab0;
  white-space: nowrap;
}

.field-list__del {
  border: none;
  background: transparent;
  color: #ea5455;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}

.field-list__del:hover {
  color: #c0392b;
}

.field-list__config {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e3e3ef;
  display: flex;
  flex-direction: column;
  gap: 6px;
  cursor: default;
}

.field-list__cfg-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b6b85;
}

.field-list__cfg-label--help {
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-list__cfg-help-icon {
  color: #9a9ab0;
  cursor: help;
}

.field-list__cfg-input {
  padding: 4px 8px;
  border: 1px solid #d8d8e3;
  border-radius: 4px;
  font-size: 12px;
}

.field-list__cfg-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #30304d;
  cursor: pointer;
}

.field-list__cfg-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.field-list__opt-head {
  display: flex;
  gap: 4px;
  font-size: 9px;
  color: #9a9ab0;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.field-list__opt-head span {
  flex: 1;
}

.field-list__opt-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.field-list__opt-input {
  flex: 1;
  min-width: 0;
}

.field-list__opt-del {
  border: none;
  background: transparent;
  color: #ea5455;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}

.field-list__opt-add {
  align-self: flex-start;
  border: 1px dashed #7367f0;
  background: transparent;
  color: #5a4fd1;
  border-radius: 4px;
  font-size: 11px;
  padding: 3px 8px;
  cursor: pointer;
}

.field-list__opt-add:hover {
  background: rgba(115, 103, 240, 0.08);
}

.field-list__cfg-grid .field-list__cfg-input {
  width: 100%;
  box-sizing: border-box;
}

</style>
